import React, { useState } from 'react';
import { X, Sparkles, ArrowRight, ArrowLeft, Loader2, Check, Plus, Filter, Trash2 } from 'lucide-react';
import { API_URL } from '../../config';

export default function AiWizardModal({ isOpen, onClose, onComplete }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Step 1 Form State
  const [brandName, setBrandName] = useState('');
  const [competitorCount, setCompetitorCount] = useState(3);
  
  // Accumulated Data State
  const [brandData, setBrandData] = useState(null);
  const [matrixData, setMatrixData] = useState(null);
  const [keywordsData, setKeywordsData] = useState(null);

  if (!isOpen) return null;

  const handlePrev = () => {
    setError('');
    if (step > 1) setStep(step - 1);
  };

  const handleLoadMoreKeywords = async () => {
    setLoading(true);
    setError('');
    try {
      const payload = {
        brandName: brandName,
        competitors: brandData.competitors,
        businessThemes: matrixData.businessThemes,
        productMatrix: matrixData.productMatrix,
        existingKeywords: keywordsData.keywords.map(k => k.keyword || k)
      };
      const res = await fetch(`${API_URL}/ai-wizard/keywords`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(payload)
      });
      if (res.status === 401) { window.location.href = '/login'; return; }
        if (!res.ok) throw new Error('API请求失败');
      const data = await res.json();
      const newKws = (data.keywords || []).map(k => {
        if (typeof k === 'string') {
          return { keyword: k, category: '未分类', ai_heat_level: '低', selected: true };
        }
        return { ...k, selected: true };
      });
      setKeywordsData({ keywords: [...keywordsData.keywords, ...newKws] });
    } catch (err) {
      setError(err.message || '追加生成失败');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    setError('');
    if (step === 1) {
      if (!brandName.trim()) {
        setError('请输入主品牌名称');
        return;
      }
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/ai-wizard/brand-discovery`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ brandName, competitorCount })
        });
        if (res.status === 401) { window.location.href = '/login'; return; }
        if (!res.ok) throw new Error('API请求失败');
        const data = await res.json();
        setBrandData(data);
        setStep(2);
      } catch (err) {
        setError(err.message || '生成失败');
      } finally {
        setLoading(false);
      }
    } else if (step === 2) {
      setLoading(true);
      try {
        const payload = {
          brandName: brandName,
          competitors: brandData.competitors
        };
        const res = await fetch(`${API_URL}/ai-wizard/matrix-dimensions`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(payload)
        });
        if (res.status === 401) { window.location.href = '/login'; return; }
        if (!res.ok) throw new Error('API请求失败');
        const data = await res.json();
        setMatrixData(data);
        setStep(3);
      } catch (err) {
        setError(err.message || '生成失败');
      } finally {
        setLoading(false);
      }
    } else if (step === 3) {
      setLoading(true);
      try {
        const payload = {
          brandName: brandName,
          competitors: brandData.competitors,
          businessThemes: matrixData.businessThemes,
          productMatrix: matrixData.productMatrix
        };
        const res = await fetch(`${API_URL}/ai-wizard/keywords`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(payload)
        });
        if (res.status === 401) { window.location.href = '/login'; return; }
        if (!res.ok) throw new Error('API请求失败');
        const data = await res.json();
        // Initialize selection state
        const kws = (data.keywords || []).map(k => {
          if (typeof k === 'string') {
            return { keyword: k, category: '未分类', ai_heat_level: '低', selected: true };
          }
          return { ...k, selected: true };
        });
        setKeywordsData({ keywords: kws });
        setStep(4);
      } catch (err) {
        setError(err.message || '生成失败');
      } finally {
        setLoading(false);
      }
    } else if (step === 4) {
      // Assemble the final project payload and pass to parent
      const selectedKws = (keywordsData?.keywords || []).filter(k => k.selected);
      
      const newProject = {
        name: `${brandName} (AI生成)`,
        brandName: brandName,
        brandAlias: brandData?.brandAlias || '',
        brandWebsite: brandData?.brandWebsite || '',
        competitors: (brandData?.competitors || []).map(c => ({
          id: `c_${Date.now()}_${Math.random()}`,
          name: c.name,
          aliases: c.alias || '',
          website: c.website || ''
        })),
        businessThemes: (matrixData?.businessThemes || []).map((t, idx) => ({
          id: `theme_${idx}_${Date.now()}`,
          name: t.name,
          subThemes: (t.children || []).map((c, cIdx) => ({
            id: `sub_${idx}_${cIdx}_${Date.now()}`,
            name: c.name,
            keywords: Array.isArray(c.keywords) ? c.keywords.join(', ') : ''
          }))
        })),
        productMappings: [],
        personas: [],
        // Pass the full structured object for selected keywords
        keywords: selectedKws.map(k => ({
          keyword: k.keyword,
          category: k.category,
          ai_heat_level: k.ai_heat_level,
          real_volumes: k.real_volumes || { baidu: null, xiaohongshu: null, douyin: null }
        }))
      };
      
      if (matrixData?.productMatrix) {
        const rows = [];
        const mainProds = matrixData.productMatrix.mainBrandProducts || [];
        mainProds.forEach((prodName, idx) => {
          rows.push({
            id: `pm_row_${Date.now()}_${idx}`,
            hostProduct: prodName,
            competitorProducts: newProject.competitors.map(c => ({
              compId: c.id,
              compName: c.name,
              prodName: matrixData.productMatrix.competitorProducts && matrixData.productMatrix.competitorProducts[c.name] ? matrixData.productMatrix.competitorProducts[c.name][idx] || '' : ''
            }))
          });
        });
        newProject.productMappings = rows;
      }
      
      onComplete(newProject);
    }
  };

  const toggleKeyword = (idx) => {
    const arr = [...keywordsData.keywords];
    arr[idx].selected = !arr[idx].selected;
    setKeywordsData({ keywords: arr });
  };

  const toggleAllKeywords = () => {
    const allSelected = keywordsData.keywords.every(k => k.selected);
    const arr = keywordsData.keywords.map(k => ({ ...k, selected: !allSelected }));
    setKeywordsData({ keywords: arr });
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-4xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-700 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="bg-indigo-100 dark:bg-indigo-900/30 p-2 rounded-lg text-indigo-600 dark:text-indigo-400">
              <Sparkles size={20} />
            </div>
            <h2 className="text-xl font-bold text-gray-800 dark:text-white">AI 智能建项向导</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X size={24} />
          </button>
        </div>

        {/* Stepper */}
        <div className="px-6 py-4 bg-gray-50 dark:bg-slate-800/50 flex justify-between relative">
           <div className="absolute top-1/2 left-10 right-10 h-0.5 bg-gray-200 dark:bg-slate-700 -z-10 -translate-y-1/2"></div>
           {[1, 2, 3, 4].map(s => (
             <div key={s} className="flex flex-col items-center">
               <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= s ? 'bg-indigo-600 text-white' : 'bg-white dark:bg-slate-700 text-gray-400 border border-gray-200 dark:border-slate-600'}`}>
                 {s}
               </div>
               <span className={`text-xs mt-2 ${step >= s ? 'text-indigo-600 dark:text-indigo-400 font-medium' : 'text-gray-400'}`}>
                 {s === 1 ? '品牌发现' : s === 2 ? '矩阵推演' : s === 3 ? '词库裂变' : '完成总览'}
               </span>
             </div>
           ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6 custom-scrollbar">
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg text-sm">
              {error}
            </div>
          )}
          
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">输入主品牌名称 (例如: 马牌轮胎)</label>
                <input
                  type="text"
                  value={brandName}
                  onChange={e => setBrandName(e.target.value)}
                  className="w-full h-12 bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-600 rounded-lg px-4 text-gray-900 dark:text-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                  placeholder="请输入主品牌名称..."
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">期望推荐的竞品数量</label>
                <select
                  value={competitorCount}
                  onChange={e => setCompetitorCount(Number(e.target.value))}
                  className="w-full h-12 bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-600 rounded-lg px-4 text-gray-900 dark:text-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                >
                  {[...Array(10)].map((_, i) => {
                    const num = i + 1;
                    return (
                      <option key={num} value={num}>
                        {num} 个竞品 {num === 3 ? '(推荐)' : ''}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div className="bg-indigo-50/50 dark:bg-indigo-900/10 p-4 rounded-xl border border-indigo-100/50 dark:border-indigo-800/30">
                 <h3 className="text-lg font-medium text-indigo-900 dark:text-indigo-300 mb-4">主品牌信息</h3>
                 <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-slate-400">品牌名称</label>
                      <div className="font-medium dark:text-white">{brandName}</div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-slate-400">别名/全称</label>
                      <input className="mt-1 w-full text-sm bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-600 rounded px-2 py-1 text-gray-900 dark:text-slate-100" 
                             value={brandData?.brandAlias || ''} 
                             onChange={(e) => setBrandData({...brandData, brandAlias: e.target.value})} />
                    </div>
                 </div>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-3">AI 推荐的竞品</h3>
                <div className="space-y-3">
                  {brandData?.competitors?.map((c, i) => (
                    <div key={i} className="flex gap-3 items-start">
                      <input className="w-1/3 text-sm bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-600 rounded px-3 py-2 text-gray-900 dark:text-slate-100" 
                             value={c.name || ''} 
                             onChange={(e) => {
                               const arr = [...brandData.competitors];
                               arr[i].name = e.target.value;
                               setBrandData({...brandData, competitors: arr});
                             }} 
                             placeholder="竞品名称"/>
                      <input className="w-1/3 text-sm bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-600 rounded px-3 py-2 text-gray-900 dark:text-slate-100" 
                             value={c.alias || ''} 
                             onChange={(e) => {
                               const arr = [...brandData.competitors];
                               arr[i].alias = e.target.value;
                               setBrandData({...brandData, competitors: arr});
                             }}
                             placeholder="竞品别名"/>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
               <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-3">AI 推荐的核心业务维度</h3>
                  <div className="space-y-4">
                    {matrixData?.businessThemes?.map((theme, i) => (
                      <div key={i} className="border border-gray-200 dark:border-slate-700 rounded-lg p-4 relative group">
                        <button 
                          onClick={() => {
                            const newData = JSON.parse(JSON.stringify(matrixData));
                            newData.businessThemes.splice(i, 1);
                            setMatrixData(newData);
                          }}
                          className="absolute top-2 right-2 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 size={16} />
                        </button>
                        <input 
                          value={theme.name}
                          onChange={(e) => {
                            const newData = JSON.parse(JSON.stringify(matrixData));
                            newData.businessThemes[i].name = e.target.value;
                            setMatrixData(newData);
                          }}
                          className="font-medium text-gray-900 dark:text-white mb-2 bg-transparent border-b border-dashed border-gray-400 focus:outline-none w-[90%]"
                          placeholder="宏观维度名称"
                        />
                        <div className="flex flex-wrap gap-2 mt-2 items-center">
                          {theme.children?.map((child, j) => (
                             <div key={j} className="flex items-center gap-1 bg-gray-100 dark:bg-slate-800 rounded px-2 py-1">
                               <input 
                                 value={child.name}
                                 onChange={(e) => {
                                   const newData = JSON.parse(JSON.stringify(matrixData));
                                   newData.businessThemes[i].children[j].name = e.target.value;
                                   setMatrixData(newData);
                                 }}
                                 className="bg-transparent text-xs text-gray-900 dark:text-slate-100 outline-none w-20"
                                 placeholder="微观维度"
                               />
                               <button 
                                 onClick={() => {
                                   const newData = JSON.parse(JSON.stringify(matrixData));
                                   newData.businessThemes[i].children.splice(j, 1);
                                   setMatrixData(newData);
                                 }}
                                 className="text-gray-400 hover:text-red-500"
                               >
                                 <X size={12} />
                               </button>
                             </div>
                          ))}
                          <button
                            onClick={() => {
                              const newData = JSON.parse(JSON.stringify(matrixData));
                              if (!newData.businessThemes[i].children) newData.businessThemes[i].children = [];
                              newData.businessThemes[i].children.push({name: ''});
                              setMatrixData(newData);
                            }}
                            className="px-2 py-1 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 text-xs rounded hover:bg-indigo-100 dark:hover:bg-indigo-500/20 transition-colors"
                          >
                            + 添加微观维度
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => {
                      const newData = JSON.parse(JSON.stringify(matrixData));
                      if (!newData.businessThemes) newData.businessThemes = [];
                      newData.businessThemes.push({name: '', children: []});
                      setMatrixData(newData);
                    }}
                    className="mt-4 w-full py-2 border border-dashed border-gray-300 dark:border-slate-600 text-gray-500 dark:text-slate-400 hover:text-indigo-600 hover:border-indigo-400 rounded-lg text-sm transition-colors"
                  >
                    + 添加宏观维度
                  </button>
               </div>
               <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-3">AI 推荐的对标产品</h3>
                  <div className="bg-gray-50 dark:bg-slate-800/50 rounded-lg p-4 text-sm space-y-3">
                     <div className="flex items-center">
                       <span className="font-medium w-24 shrink-0 text-gray-500">主品牌产品:</span>
                       <input 
                         type="text"
                         value={matrixData?.productMatrix?.mainBrandProducts?.join(', ') || ''}
                         onChange={(e) => {
                           const newData = JSON.parse(JSON.stringify(matrixData));
                           if(!newData.productMatrix) newData.productMatrix = {};
                           newData.productMatrix.mainBrandProducts = e.target.value.split(',').map(s=>s.trim()).filter(Boolean);
                           setMatrixData(newData);
                         }}
                         className="flex-1 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded px-2 py-1 text-gray-900 dark:text-slate-100"
                       />
                     </div>
                     {matrixData?.productMatrix?.competitorProducts && Object.entries(matrixData.productMatrix.competitorProducts).map(([compName, prods], i) => (
                        <div key={i} className="flex items-center">
                          <span className="font-medium w-24 shrink-0 text-gray-500">{compName}:</span>
                          <input 
                             type="text"
                             value={(prods || []).join(', ')}
                             onChange={(e) => {
                               const newData = JSON.parse(JSON.stringify(matrixData));
                               newData.productMatrix.competitorProducts[compName] = e.target.value.split(',').map(s=>s.trim()).filter(Boolean);
                               setMatrixData(newData);
                             }}
                             className="flex-1 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded px-2 py-1 text-gray-900 dark:text-slate-100"
                          />
                        </div>
                     ))}
                  </div>
               </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                 <div>
                   <h2 className="text-xl font-bold text-gray-900 dark:text-white">选词工作台</h2>
                   <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
                     已生成 {keywordsData?.keywords?.length || 0} 个监控词，当前选中 {(keywordsData?.keywords || []).filter(k=>k.selected).length} 个
                   </p>
                 </div>
                 <button 
                   onClick={handleLoadMoreKeywords}
                   disabled={loading}
                   className="flex items-center space-x-2 px-4 py-2 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors text-sm font-medium disabled:opacity-50"
                 >
                   {loading ? <Loader2 size={16} className="animate-spin"/> : <Plus size={16}/>}
                   <span>继续深挖 25 词</span>
                 </button>
              </div>

              <div className="border border-gray-200 dark:border-slate-700 rounded-xl overflow-hidden shadow-sm">
                <div className="max-h-[50vh] overflow-y-auto">
                  <table className="w-full text-sm text-left">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-slate-800 dark:text-gray-300 sticky top-0 z-10 shadow-sm">
                      <tr>
                        <th scope="col" className="p-4 w-12">
                          <div className="flex items-center">
                            <input 
                              type="checkbox" 
                              checked={keywordsData?.keywords?.length > 0 && keywordsData.keywords.every(k => k.selected)}
                              onChange={toggleAllKeywords}
                              className="w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 rounded focus:ring-indigo-500 dark:focus:ring-indigo-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600 cursor-pointer"
                            />
                          </div>
                        </th>
                        <th scope="col" className="px-6 py-3 font-semibold">关键词 (Keyword)</th>
                        <th scope="col" className="px-6 py-3 font-semibold w-40">意图分类</th>
                        <th scope="col" className="px-6 py-3 font-semibold w-24">估算热度</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-slate-700/50 bg-white dark:bg-slate-900/50">
                      {(keywordsData?.keywords || []).map((item, idx) => (
                        <tr key={idx} className={`hover:bg-gray-50 dark:hover:bg-slate-800/80 transition-colors cursor-pointer ${item.selected ? '' : 'opacity-60'}`} onClick={() => toggleKeyword(idx)}>
                          <td className="p-4" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center">
                              <input 
                                type="checkbox" 
                                checked={!!item.selected}
                                onChange={() => toggleKeyword(idx)}
                                className="w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 rounded focus:ring-indigo-500 dark:focus:ring-indigo-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600 cursor-pointer"
                              />
                            </div>
                          </td>
                          <td className="px-6 py-3 font-medium text-gray-900 dark:text-gray-200">{item.keyword || item}</td>
                          <td className="px-6 py-3 text-gray-500 dark:text-gray-400">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                              {item.category || '未分类'}
                            </span>
                          </td>
                          <td className="px-6 py-3">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              item.ai_heat_level === '高' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' :
                              item.ai_heat_level === '中' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300' :
                              'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                            }`}>
                              {item.ai_heat_level || '低'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 dark:border-slate-700 flex justify-between items-center bg-gray-50 dark:bg-slate-800/80 rounded-b-2xl">
          <button 
            onClick={onClose}
            className="px-4 py-2 text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-200 font-medium"
          >
            取消
          </button>
          
          <div className="flex space-x-3">
            {step > 1 && (
              <button 
                onClick={handlePrev}
                disabled={loading}
                className="flex items-center space-x-2 bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-slate-600 px-4 py-2 rounded-lg font-medium transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowLeft size={18} /><span>上一步</span>
              </button>
            )}
            <button 
              onClick={handleNext}
              disabled={loading || (step === 4 && (!keywordsData?.keywords || keywordsData.keywords.filter(k=>k.selected).length === 0))}
            className="flex items-center space-x-2 bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-lg font-medium transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <><Loader2 size={18} className="animate-spin" /><span>加载中...</span></>
            ) : step === 4 ? (
              <><Check size={18} /><span>保存选中 ({keywordsData?.keywords?.filter(k=>k.selected).length || 0}) 至项目</span></>
            ) : (
              <><span>下一步</span><ArrowRight size={18} /></>
            )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
